import { useState, useCallback } from 'react';
import { useLoaderData, useParams } from 'react-router';
import {
	Box,
	Chip,
	Typography,
	Paper,
	Button,
	IconButton,
	Switch,
	Tooltip,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockIcon from '@mui/icons-material/Lock';
import PromptManageDrawer from '../components/Prompt/PromptManageDrawer';
import PromptVersionsDrawer from '../components/Prompt/PromptVersionsDrawer';
import PromptVersionIndicator from '../components/Prompt/PromptVersionIndicator';
import PromptDeleteDialog from '../components/Prompt/PromptDeleteDialog';
import { usePrompt } from '../hooks/Prompt';
import promptService from '../services/promptService';

/**
 * Loader function to fetch prompts data
 */
export const loader = async ({ params }) => {
	const { tenantId } = params;

	try {
		// Request tenant default version information with prompts
		const response = await promptService.getPrompts(tenantId, {
			includeTenantDefault: true,
		});

		// Prepare data for the data grid
		const formattedPrompts = response.map((prompt) => ({
			id: prompt.id,
			name: prompt.name,
			content: prompt.content,
			promptType: prompt.prompt_type_name,
			version: prompt.prompt_version,
			latestVersion: prompt.latest_version_number,
			hasNewerVersion: prompt.has_newer_version,
			active: prompt.active,
			hierarchyLevel: prompt.hierarchy_level,
			createdAt: prompt.created_at,
			updatedAt: prompt.updated_at,
			// Tenant default version naming
			isSystemPrompt: prompt.tenant_id === null,
			hasTenantOverride: !!prompt.tenant_default_version_number,
			tenantOverrideVersion: prompt.tenant_default_version_number,
			isTenantOverrideBehind: prompt.is_tenant_default_behind,
			_original: prompt, // Store original data for updates
		}));

		return { prompts: formattedPrompts, error: null };
	} catch (error) {
		console.error('Error loading prompts data:', error);
		return {
			prompts: [],
			error: 'Failed to load prompts. Please try again later.',
		};
	}
};

/**
 * Prompts page for the Investing app
 * Displays information about AI prompts
 */
const PromptsPage = () => {
	const theme = useTheme();
	const { tenantId } = useParams();
	const { prompts: initialPrompts } = useLoaderData() || { prompts: [] };
	const [localPrompts, setLocalPrompts] = useState(initialPrompts || []);

	// State for dialogs
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedPromptForEdit, setSelectedPromptForEdit] = useState(null);
	const [selectedPromptForVersions, setSelectedPromptForVersions] =
		useState(null);
	const [selectedPromptForDelete, setSelectedPromptForDelete] = useState(null);

	// State for the actions menu
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);

	// Use the prompt hook for toggle and delete operations
	const { toggleActive, deletePrompt } = usePrompt(tenantId);

	// Format prompt data consistently to match the expected format for the DataGrid
	const formatPrompt = (prompt) => {
		if (!prompt) return null;

		// If prompt is already in the correct format, return it as is
		if (prompt._original && prompt.promptType) {
			return prompt;
		}

		// Otherwise, format it from API response format to our local format
		return {
			id: prompt.id,
			name: prompt.name,
			content: prompt.content,
			promptType: prompt.prompt_type_name || prompt.promptType,
			version: prompt.prompt_version || prompt.version,
			latestVersion: prompt.latest_version_number || prompt.latestVersion,
			hasNewerVersion:
				prompt.has_newer_version || prompt.hasNewerVersion || false,
			active: prompt.active,
			hierarchyLevel: prompt.hierarchy_level || prompt.hierarchyLevel,
			createdAt: prompt.created_at || prompt.createdAt,
			updatedAt: prompt.updated_at || prompt.updatedAt,
			tenantId: prompt.tenant_id || null,
			// Include tenant default version fields
			hasTenantOverride:
				prompt.tenant_default_version_number !== undefined
					? !!prompt.tenant_default_version_number
					: !!prompt.hasTenantOverride,
			tenantOverrideVersion:
				prompt.tenant_default_version_number ||
				prompt.tenantOverrideVersion ||
				null,
			isTenantOverrideBehind:
				prompt.is_tenant_default_behind ||
				prompt.isTenantOverrideBehind ||
				false,
			isSystemPrompt:
				prompt.tenant_id === null || prompt.isSystemPrompt || false,
			_original: prompt._original || prompt,
		};
	};

	// Handle successful updates from child components
	const handleUpdateSuccess = useCallback(
		(updatedPrompt, action = 'update') => {
			// Ensure we have the updated prompt data
			if (!updatedPrompt) {
				console.error('No updated prompt data received');
				return;
			}

			const formattedPrompt = formatPrompt(updatedPrompt);

			switch (action) {
				case 'create':
				case 'fork': {
					setLocalPrompts((prevPrompts) => [formattedPrompt, ...prevPrompts]);
					break;
				}

				case 'update':
				case 'version_change': {
					setLocalPrompts((prevPrompts) =>
						prevPrompts.map((prompt) =>
							prompt.id === formattedPrompt.id ? formattedPrompt : prompt
						)
					);
					break;
				}

				case 'delete': {
					setLocalPrompts((prevPrompts) =>
						prevPrompts.filter((prompt) => prompt.id !== updatedPrompt.id)
					);
					break;
				}

				default:
					console.warn('Unknown action type in handleUpdateSuccess:', action);
					break;
			}
		},
		[]
	);

	const handleEditPrompt = useCallback((prompt) => {
		setSelectedPromptForEdit(prompt);
		setEditDialogOpen(true);
	}, []);

	const handleViewVersions = useCallback((prompt) => {
		setSelectedPromptForVersions(prompt);
		setVersionsDialogOpen(true);
	}, []);

	const handleDeletePrompt = useCallback((prompt) => {
		setSelectedPromptForDelete(prompt);
		setDeleteDialogOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(
		async (prompt) => {
			if (!prompt) return;

			// Optimistically remove the prompt from the UI
			setLocalPrompts((prevPrompts) =>
				prevPrompts.filter((p) => p.id !== prompt.id)
			);

			try {
				await deletePrompt(prompt.id);
				// The actual removal is already done optimistically
			} catch (err) {
				// Revert the UI if the delete operation fails
				setLocalPrompts((prevPrompts) =>
					[...prevPrompts, prompt].sort((a, b) => a.name.localeCompare(b.name))
				);

				console.error('Failed to delete prompt:', err);
			}
		},
		[deletePrompt]
	);

	const handleOpenMenu = useCallback((event, row) => {
		setAnchorEl(event.currentTarget);
		setSelectedRowForMenu(row);
	}, []);

	const handleCloseMenu = useCallback(() => {
		setAnchorEl(null);
		setSelectedRowForMenu(null);
	}, []);

	const handleAddNewPrompt = useCallback(() => {
		setSelectedPromptForEdit(null);
		setEditDialogOpen(true);
	}, []);

	const handleActiveToggle = useCallback(
		async (prompt) => {
			const newActiveState = !prompt.active;

			// Optimistically update the UI
			setLocalPrompts((prevPrompts) =>
				prevPrompts.map((p) =>
					p.id === prompt.id
						? {
								...p,
								active: newActiveState,
								_original: {
									...p._original,
									active: newActiveState,
								},
							}
						: p
				)
			);

			try {
				await toggleActive(prompt.id, newActiveState);
			} catch (err) {
				// Revert on error
				setLocalPrompts((prevPrompts) =>
					prevPrompts.map((p) =>
						p.id === prompt.id
							? {
									...p,
									active: prompt.active,
									_original: {
										...p._original,
										active: prompt.active,
									},
								}
							: p
					)
				);
				console.error('Failed to toggle prompt status:', err);
			}
		},
		[toggleActive]
	);

	const handleCloseEditDialog = useCallback(() => {
		setEditDialogOpen(false);
		setSelectedPromptForEdit(null);
	}, []);

	const handleCloseVersionsDialog = useCallback(() => {
		setVersionsDialogOpen(false);
		setSelectedPromptForVersions(null);
	}, []);

	// Column definitions
	const columns = [
		{
			field: 'actions',
			headerName: '',
			width: 70,
			sortable: false,
			renderCell: (params) => (
				<Box>
					<Tooltip title='Actions'>
						<IconButton
							onClick={(event) => handleOpenMenu(event, params.row)}
							size='small'
							aria-label='prompt actions'
						>
							<MoreVertIcon />
						</IconButton>
					</Tooltip>

					<Menu
						anchorEl={anchorEl}
						open={Boolean(anchorEl) && selectedRowForMenu?.id === params.row.id}
						onClose={handleCloseMenu}
						onClick={handleCloseMenu}
						PaperProps={{
							elevation: 1,
							sx: { minWidth: 180 },
						}}
					>
						{!selectedRowForMenu?.isSystemPrompt && (
							<MenuItem onClick={() => handleEditPrompt(selectedRowForMenu)}>
								<ListItemIcon>
									<EditIcon fontSize='small' color='primary' />
								</ListItemIcon>
								<ListItemText>Edit</ListItemText>
							</MenuItem>
						)}

						<MenuItem onClick={() => handleViewVersions(selectedRowForMenu)}>
							<ListItemIcon>
								<HistoryIcon fontSize='small' color='info' />
							</ListItemIcon>
							<ListItemText>View Versions</ListItemText>
						</MenuItem>

						{!selectedRowForMenu?.isSystemPrompt && (
							<MenuItem onClick={() => handleDeletePrompt(selectedRowForMenu)}>
								<ListItemIcon>
									<DeleteIcon fontSize='small' color='error' />
								</ListItemIcon>
								<ListItemText>Delete</ListItemText>
							</MenuItem>
						)}
					</Menu>
				</Box>
			),
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 2,
			editable: true,
			renderCell: (params) => (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{params.value}
					{params.row.isSystemPrompt && (
						<Chip
							label='Default'
							color='secondary'
							size='small'
							icon={<LockIcon fontSize='small' />}
						/>
					)}
				</Box>
			),
		},
		{
			field: 'promptType',
			headerName: 'Prompt Type',
			flex: 1,
			editable: false,
		},
		{
			field: 'version',
			headerName: 'Version',
			flex: 0.5,
			width: 90,
			editable: false,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params) => {
				// If it's a system prompt and tenant override version exists, show that
				const displayVersion =
					params.row.isSystemPrompt && params.row.tenantOverrideVersion
						? params.row.tenantOverrideVersion
						: params.value;
				return (
					<PromptVersionIndicator
						version={displayVersion}
						hasNewerVersion={params.row.hasNewerVersion}
						latestVersion={params.row.latestVersion}
						isTenantOverride={
							params.row.hasTenantOverride &&
							params.row.tenantOverrideVersion === displayVersion
						}
						isActive={params.row.version === displayVersion}
					/>
				);
			},
		},
		{
			field: 'active',
			headerName: 'Active',
			flex: 1,
			editable: false,
			renderCell: (params) => (
				<Switch
					checked={params.row.active}
					onChange={() => handleActiveToggle(params.row)}
					color='primary'
					disabled={params.row.isSystemPrompt}
				/>
			),
		},
		{
			field: 'hierarchyLevel',
			headerName: 'Hierarchy Level',
			flex: 1,
			editable: false,
		},
	];

	return (
		<Box sx={{ p: 2 }}>
			<PromptManageDrawer
				open={editDialogOpen}
				onClose={handleCloseEditDialog}
				prompt={selectedPromptForEdit}
				onUpdateSuccess={handleUpdateSuccess}
			/>
			<PromptVersionsDrawer
				open={versionsDialogOpen}
				onClose={handleCloseVersionsDialog}
				prompt={selectedPromptForVersions}
				tenantId={tenantId}
				onVersionChange={(updatedPrompt, action) =>
					handleUpdateSuccess(updatedPrompt, action)
				}
			/>
			<PromptDeleteDialog
				open={deleteDialogOpen}
				prompt={selectedPromptForDelete}
				onClose={() => setDeleteDialogOpen(false)}
				onConfirm={handleConfirmDelete}
			/>
			<Paper
				sx={{ p: 2, mb: 2, backgroundColor: theme.palette.background.paper }}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 1,
					}}
				>
					<Typography variant='h4'>All Prompts</Typography>
					<Button
						variant='contained'
						startIcon={<AddIcon />}
						onClick={handleAddNewPrompt}
					>
						Add New Prompt
					</Button>
				</Box>
				<Box
					sx={{
						height: '100%',
						width: '100%',
						'& .MuiDataGrid-root': {
							border: `1px solid ${theme.palette.divider}`,
							color: theme.palette.text.primary,
							backgroundColor: theme.palette.background.paper,
						},
						'& .MuiDataGrid-columnHeaders': {
							backgroundColor: theme.palette.action.hover,
						},
						'& .MuiDataGrid-cell:focus': {
							outline: 'none',
						},
						minHeight: '400px',
					}}
				>
					<DataGrid
						rows={localPrompts}
						columns={columns}
						experimentalFeatures={{ newEditingApi: true }}
						disableSelectionOnClick
						autoHeight
						initialState={{
							sorting: {
								sortModel: [{ field: 'name', sort: 'asc' }],
							},
						}}
						components={{
							NoRowsOverlay: () => (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										height: '100%',
									}}
								>
									<Typography variant='body1'>No prompts found</Typography>
								</Box>
							),
						}}
					/>
				</Box>
			</Paper>
		</Box>
	);
};

export default PromptsPage;
